/**
 * 正規化 draft-plan candidate.groupMtrOptions（後端形狀可能略異）。
 * @returns {Array<{
 *   sourceGroupMaterialId: string,
 *   sourceGroupMtrNo: string,
 *   sourceGroupMtrName: string,
 *   options: { mtrId: string, mtrNo: string, mtrName: string }[],
 * }>}
 */
export function normalizeDraftPlanGroupMtrOptions(groupMtrOptions) {
  const list = Array.isArray(groupMtrOptions) ? groupMtrOptions : [];

  return list.map((group, index) => {
    const source = group?.sourceGroupMtr
      ?? group?.sourceGroupMaterial
      ?? group?.groupMtr
      ?? {};
    const sourceGroupMaterialId = String(
      source?.id
      ?? group?.sourceGroupMaterialId
      ?? group?.sourceGroupMtrId
      ?? `group-${index}`,
    );
    const rawOptions = group?.options
      ?? group?.materials
      ?? group?.candidates
      ?? [];
    const options = (Array.isArray(rawOptions) ? rawOptions : []).map((option) => {
      const mtr = option?.mtr ?? option;
      return {
        mtrId: String(mtr?.id ?? mtr?.mtrId ?? ''),
        mtrNo: String(mtr?.mtrNo ?? ''),
        mtrName: String(mtr?.name ?? mtr?.mtrName ?? ''),
      };
    }).filter((option) => option.mtrId);

    return {
      sourceGroupMaterialId,
      sourceGroupMtrNo: String(source?.mtrNo ?? ''),
      sourceGroupMtrName: String(source?.name ?? source?.mtrName ?? ''),
      options,
    };
  }).filter((group) => group.sourceGroupMaterialId);
}

/**
 * draft-plan `products[].candidates[]` → 物料計劃列（扁平）。
 * @param {object|null|undefined} draftPlan
 * @returns {{ rows: object[], noBomShortages: object[] }}
 */
export function mapDraftPlanToManufacturingRows(draftPlan) {
  const products = Array.isArray(draftPlan?.products) ? draftPlan.products : [];
  const rows = [];
  const noBomShortages = [];

  products.forEach((productBlock, productIndex) => {
    const lineId = productBlock?.customerOrderLineId ?? '';
    const product = productBlock?.product ?? {};
    const productLabel = [
      product?.productNo,
      product?.name,
    ].filter(Boolean).join(' ');

    (productBlock?.noBomShortages ?? []).forEach((item, shortageIndex) => {
      const mtr = item?.mtr ?? {};
      noBomShortages.push({
        id: `no-bom-${lineId || productIndex}-${mtr?.id || shortageIndex}`,
        productLabel,
        mtrId: mtr?.id ?? '',
        mtrNo: mtr?.mtrNo ?? '',
        mtrName: mtr?.name ?? '',
        requiredQty: item?.requiredQty ?? null,
        reason: item?.reason ?? '',
      });
    });

    (productBlock?.candidates ?? []).forEach((candidate, candidateIndex) => {
      const mtr = candidate?.mtr ?? {};
      const bom = candidate?.bom ?? {};
      const mtrId = String(mtr?.id ?? '');
      const groupMtrOptions = normalizeDraftPlanGroupMtrOptions(
        candidate?.groupMtrOptions,
      );
      const requiredQty = Number(candidate?.requiredQty);
      const shortageQty = Number(candidate?.shortageQty);
      const suggested = Number(candidate?.suggestedManufacturingQty);
      const allocated = Number(candidate?.allocatedInProgressQty);
      const planRequired = Number.isFinite(requiredQty) ? requiredQty : 0;
      const planShortage = Number.isFinite(shortageQty) ? shortageQty : 0;
      const planSuggested = Number.isFinite(suggested) ? suggested : planShortage;
      const planAllocated = Number.isFinite(allocated) ? allocated : null;

      rows.push({
        id: [
          lineId || `line-${productIndex}`,
          mtrId || `mtr-${candidateIndex}`,
          bom?.id || `bom-${candidateIndex}`,
        ].join(':'),
        customerOrderLineId: lineId || null,
        productId: product?.id ?? null,
        productLabel,
        mtrId,
        mtrNo: String(mtr?.mtrNo ?? ''),
        mtrName: String(mtr?.name ?? ''),
        bomId: bom?.id ?? null,
        bomCode: bom?.code ?? '',
        bomName: bom?.name ?? '',
        bomVersion: bom?.version ?? '',
        unitRequiredQty: null,
        totalRequiredQty: planRequired,
        shortageQty: planShortage,
        manufacturingQty: planSuggested,
        warehouseQty: null,
        inProductionQty: planAllocated,
        groupMtrOptions,
        // 替代料／實際料強制手選：進頁不預填 options[0]
        bomMaterialSelections: [],
        selectedMtrId: '',
        selectedMtrNo: '',
        selectedMtrName: '',
        candidates: groupMtrOptions.length === 1
          ? groupMtrOptions[0].options
          : [],
        selected: planSuggested > 0,
      });
    });
  });

  return { rows, noBomShortages };
}
